import { useState } from 'react';
import { styled } from 'styled-components';
import { Icon, Modal, SpaceBetween, Typography, tk } from '@sunwebgroup/ui-components';
import { useIntl } from '@/intl';
import { useCampaignDetails } from '@/scopes/search/search/domain/campaign-details/hooks';

interface CampaignDetailsProps {
  id: string;
  title: string;
}

interface CampaignDetailsResultProps extends CampaignDetailsProps {
  onClose: () => void;
}

export function CampaignDetails(props: CampaignDetailsProps) {
  const [showModal, setShowModal] = useState(false);

  const onClose = () => {
    setShowModal(false);
  };

  const openModal = () => {
    setShowModal(true);
  };

  return (
    <>
      <CampaignInfoButton onClick={openModal}>
        <Icon glyph="IconInformationCircle" size="sm" />
      </CampaignInfoButton>
      {showModal && <CampaignDetailsResult onClose={onClose} {...props} />}
    </>
  );
}

function CampaignDetailsResult({ onClose, id, title }: CampaignDetailsResultProps) {
  const intl = useIntl();
  const { isLoading, error, campaignDetails } = useCampaignDetails(id);

  return (
    <Modal.Root open onClose={onClose}>
      <Modal.Content $size="md">
        <Modal.Header>
          <Modal.HeaderTitle text={title} />
          <Modal.HeaderCloseButton />
        </Modal.Header>
        <Modal.Body>
          {isLoading && <Modal.Spinner />}
          {error && (
            <ErrorMessage direction="y" spacing={tk('spacing_sm')} alignItems="center" justifyContent="center">
              <Icon glyph="IconWarningTriangle" size="md" />
              <Typography as="p" $variant="body-lg-bold">
                {intl.formatMessage({ id: 'campaign_details_error_msg' })}
              </Typography>
            </ErrorMessage>
          )}
          {campaignDetails && (
            <CampaignDetailsContent
              as="div"
              $variant="body-md"
              dangerouslySetInnerHTML={{ __html: campaignDetails.content }}
            />
          )}
        </Modal.Body>{' '}
      </Modal.Content>
    </Modal.Root>
  );
}

const CampaignInfoButton = styled.button`
  margin-left: ${tk('spacing_xs')};
  position: relative;

  ${Icon} {
    vertical-align: middle;
  }
`;

const ErrorMessage = styled(SpaceBetween)`
  text-align: center;
  margin: 0 ${tk('spacing_md')};
  color: ${tk('color_semantic_text_strong')};

  @media ${tk('mq_tablet')} {
    margin: 0 ${tk('spacing_5xl')};
    max-height: calc(
      100vh - 120px
    ); // Set max height with viewport height minus modal header (56px) and spacing_4xl (64px) from Modal parent element, so content is not scrollable on smaller screens.
    height: 684px; // Set height with the modal's height (740px) from design minus modal header (56px).
  }
`;

const CampaignDetailsContent = styled(Typography)`
  padding: ${tk('spacing_md')};
`;
